"use client";

import React from "react";
import { MapPin, Anchor, Ship, Clock, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const regions = [{
  id: "panama",
  name: "Panama Canal\xA0",
  description: "Strategic transit hub specializing in transit coordination and maintaining a robust network of trusted maritime suppliers for vessels crossing the isthmus.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1576388/img/panama_canal_terminal.png"
}, {
  id: "venezuela",
  name: "Venezuela Ports",
  description: "Dedicated support for oil tankers and bulk carriers, focusing on the specialized coordination of technical procurement and maritime services.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1576388/img/venezuela_oil_terminal.png"
}, {
  id: "spain",
  name: "Northern Spain Ports",
  description: "Gateway to European markets, stressing the rapid coordination of heavy spares and specialized technical services through our regional network.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1576388/img/spain_cargo_port.png"
}, {
  id: "colombia",
  name: "Colombia Ports",
  description: "Key Caribbean and Pacific access points providing reliable logistics coordination for fresh provisions and deck stores via trusted local partners.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1576388/img/colombia_logistics_hub.png"
}];
const regionSpecs = [{
  regionId: "panama",
  label: "Average Turnaround",
  value: "12-24 hrs",
  icon: "clock"
}, {
  regionId: "panama",
  label: "Vessel Capacity",
  value: "Neopanamax",
  icon: "ship"
}, {
  regionId: "panama",
  label: "Core Provisions",
  value: "Full Range + Bunkers",
  icon: "anchor"
}, {
  regionId: "venezuela",
  label: "Average Turnaround",
  value: "24-48 hrs",
  icon: "clock"
}, {
  regionId: "venezuela",
  label: "Vessel Capacity",
  value: "VLCC / Suezmax",
  icon: "ship"
}, {
  regionId: "venezuela",
  label: "Core Provisions",
  value: "Technical Spares",
  icon: "anchor"
}, {
  regionId: "spain",
  label: "Average Turnaround",
  value: "18-36 hrs",
  icon: "clock"
}, {
  regionId: "spain",
  label: "Vessel Capacity",
  value: "Capesize",
  icon: "ship"
}, {
  regionId: "spain",
  label: "Core Provisions",
  value: "Heavy Equipment",
  icon: "anchor"
}, {
  regionId: "colombia",
  label: "Average Turnaround",
  value: "16-30 hrs",
  icon: "clock"
}, {
  regionId: "colombia",
  label: "Vessel Capacity",
  value: "Panamax",
  icon: "ship"
}, {
  regionId: "colombia",
  label: "Core Provisions",
  value: "Fresh & Deck Stores",
  icon: "anchor"
}];
export default function RegionalLogisticsHubs() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: gridRef,
    isVisible: isGridVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const renderIcon = (iconName: string) => {
    switch (iconName) {
      case "clock":
        return <Clock className="size-4 text-primary-foreground/70" />;
      case "ship":
        return <Ship className="size-4 text-primary-foreground/70" />;
      case "anchor":
        return <Anchor className="size-4 text-primary-foreground/70" />;
      default:
        return <MapPin className="size-4 text-primary-foreground/70" />;
    }
  };
  return <section id="regional-logistics-hubs" className="bg-background py-24">
      <div className="container mx-auto px-4 md:px-6">
        
        <div ref={headerRef} className={`mb-16 max-w-3xl transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 tracking-tight font-serif">&nbsp; &nbsp; &nbsp;Global Network <br />Regional Logistics Hubs</h2>
          <p className="text-lg text-muted-foreground">
            Venchandlers Nexus specializes in the brokerage and coordination of vessels with trusted maritime suppliers and service providers across key chokepoints in the Atlantic and Caribbean.
          </p>
        </div>

        <div ref={gridRef} className={`grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 transition-all duration-1000 ease-out delay-200 ${isGridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {regions.map((region, i) => {
          const specs = regionSpecs.filter(spec => spec.regionId === region.id);
          return <Card key={region.id} data-index={i} className="bg-primary text-primary-foreground rounded-none border-primary/20 shadow-xl overflow-hidden flex flex-col group">
                <div className="relative h-48 overflow-hidden">
                  <div className="absolute inset-0 bg-primary/40 mix-blend-multiply z-10 transition-opacity group-hover:opacity-20" />
                  <img src={region.image} alt={region.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute top-4 left-4 z-20 bg-background/10 backdrop-blur-md border border-white/10 px-3 py-1 flex items-center gap-2">
                    <MapPin className="size-4 text-white" />
                    <span className="text-xs font-medium text-white uppercase tracking-wider">Active Hub</span>
                  </div>
                </div>
                
                <CardHeader className="pb-4">
                  <CardTitle className="text-xl font-serif text-white">{region.name}</CardTitle>
                  <CardDescription className="text-primary-foreground/70 text-sm leading-relaxed">
                    {region.description}
                  </CardDescription>
                </CardHeader>
                
                <CardContent className="mt-auto pt-0 pb-0 px-0">
                  <Table className="border-t border-primary-foreground/10">
                    <TableHeader>
                      <TableRow className="border-b border-primary-foreground/10 hover:bg-transparent">
                        <TableHead className="text-primary-foreground/50 text-xs uppercase tracking-wider h-8 px-6">Specification</TableHead>
                        <TableHead className="text-primary-foreground/50 text-xs uppercase tracking-wider h-8 px-6 text-right">Details</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {specs.map((spec, j) => <TableRow key={`${spec.regionId}-${j}`} data-index={j} className={`border-b border-primary-foreground/5 hover:bg-primary-foreground/5 transition-colors ${j % 2 === 0 ? 'bg-black/20' : 'bg-transparent'}`}>
                          <TableCell className="px-6 py-3">
                            <div className="flex items-center gap-2">
                              {renderIcon(spec.icon)}
                              <span className="text-sm font-medium text-primary-foreground/90">{spec.label}</span>
                            </div>
                          </TableCell>
                          <TableCell className="px-6 py-3 text-right text-sm text-primary-foreground/80">
                            {spec.value}
                          </TableCell>
                        </TableRow>)}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>;
        })}
        </div>

        <div className="mt-16 flex justify-center">
          <Button asChild size="lg" className="rounded-none px-8">
            <Link to="/#request-quote-home">
              Request Regional Procurement Quote
              <ArrowRight className="ml-2 size-4" />
            </Link>
          </Button>
        </div>

      </div>
    </section>;
}